import { useState, useEffect } from "react";
import { IconSearch, IconX } from "@tabler/icons-react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import SearchResultItem from "./SearchResultItem";
import { useTopics } from "@/hooks/useQueryHooks";
import { useNavigate } from "react-router-dom";

interface SearchModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onClose?: () => void;
}

const SearchModal = ({ isOpen, onOpenChange, onClose }: SearchModalProps) => {
  const [searchQuery, setSearchQuery] = useState("");
  const navigate = useNavigate();
  
  const { data: topics = [] } = useTopics();

  // Filter topics based on search query
  const filteredTopics = topics.filter(topic => 
    topic.title.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Get recent topics (last 5 topics sorted by creation date)
  const recentTopics = topics
    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
    .slice(0, 5);

  const handleTopicClick = (topicId: string) => {
    navigate(`/topic/${topicId}`);
    onClose?.();
    onOpenChange(false);
  };

  const handleClose = () => {
    setSearchQuery("");
    onOpenChange(false);
    onClose?.();
  };

  // Reset search query when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSearchQuery("");
    }
  }, [isOpen]);

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent 
        className="max-w-2xl w-full mx-auto p-0 gap-0 bg-white rounded-xl overflow-hidden"
        style={{ backgroundColor: 'white' }}
        hideCloseButton
      >
        {/* Search Header */}
        <div className="flex items-center gap-3 p-3 py-4 border-b border-gray-200">
          <IconSearch className="w-5 h-5 text-gray-400" stroke={1.5} />
          <input
            type="text"
            placeholder="Search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="flex-1 text-lg text-gray-800 placeholder-gray-400 bg-transparent border-none outline-none"
            autoFocus
          />
          <button
            onClick={handleClose}
            className="p-1 hover:bg-gray-100 rounded-md transition-colors"
          >
            <IconX className="w-5 h-5 text-gray-400" stroke={1.5} />
          </button>
        </div>

        {/* Search Results */}
        <ScrollArea className="h-96">
          <div className="py-2">
            {!searchQuery && (
              <>
                <div className="px-6 py-3">
                  <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wide">
                    Recents
                  </h3>
                </div>
                {recentTopics.map((topic) => (
                  <SearchResultItem
                    key={topic.id}
                    title={topic.title}
                    type="topic"
                    createdAt={topic.created_at}
                    onClick={() => handleTopicClick(topic.id)}
                  />
                ))}
              </>
            )}

            {searchQuery && (
              <>
                {filteredTopics.length > 0 && (
                  <>
                    <div className="px-6 py-3">
                      <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wide">
                        Topics
                      </h3>
                    </div>
                    {filteredTopics.map((topic) => (
                      <SearchResultItem
                        key={topic.id}
                        title={topic.title}
                        type="topic"
                        createdAt={topic.created_at}
                        onClick={() => handleTopicClick(topic.id)}
                      />
                    ))}
                  </>
                )}

                {filteredTopics.length === 0 && (
                  <div className="px-6 py-8 text-center">
                    <p className="text-gray-500">No results found for "{searchQuery}"</p>
                  </div>
                )}
              </>
            )}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};

export default SearchModal;